import { parseDrawDate } from './dateFormat.js';

const normalizeAmount = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.-]/g, '');
    const parsed = Number(cleaned);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

export const unwrapOperationData = (responseData) => {
  const payload = responseData?.payload ?? responseData;
  if (payload && typeof payload === 'object' && Object.prototype.hasOwnProperty.call(payload, 'data')) {
    return payload.data ?? null;
  }
  return payload ?? null;
};

// Pick a localized string from a {language: text} dictionary returned by the API.
// Falls back to the first available value, then to fallback string.
export const pickLocalized = (dict, language, fallback = '') => {
  if (!dict || typeof dict !== 'object') return fallback;

  // Normalize shorter locale codes to full language names if needed
  const normMap = { en: 'English', fr: 'French', es: 'Spanish' };
  const normalizedLang = normMap[language] || language;

  if (language && dict[language]) return dict[language];
  if (normalizedLang && dict[normalizedLang]) return dict[normalizedLang];

  // Common variants
  const variants = [language, normalizedLang, 'English', 'en', 'EN'].filter(Boolean);
  for (const v of variants) {
    if (dict[v]) return dict[v];
  }
  const firstKey = Object.keys(dict)[0];
  return firstKey ? dict[firstKey] : fallback;
};

export const mapDrawFromApi = (data, options = {}) => {
  if (!data) return null;

  const { fallbackTitle = 'Draw' } = options;

  const rawCutoffPassed = data.cutOffDatePassed ?? data.CutOffDatePassed ?? data.cutoff_date_passed ?? false;
  const drawDateValue = data.draw_date || data.drawDate || data.nextDrawDate || data.next_draw_date;
  const utcOffset = data.utcOffset ?? data.utc_offset ?? null;
  const normalizedDrawDateValue = drawDateValue && utcOffset != null
    ? parseDrawDate({ draw_date: drawDateValue, utc_offset: utcOffset })?.toISOString() || drawDateValue
    : drawDateValue;
  // Defensive: if backend reports cutoff passed but draw_date is still in the
  // future (can happen briefly when the next-draw resolver hasn't rolled over
  // after a draw's cutoff), trust the future draw_date and treat as open.
  // A 1-minute skew buffer avoids flipping state for draws that just closed.
  let cutoffPassed = rawCutoffPassed === true;
  if (cutoffPassed && normalizedDrawDateValue) {
    const drawTs = Date.parse(normalizedDrawDateValue);
    if (!Number.isNaN(drawTs) && drawTs - Date.now() > 60 * 1000) {
      cutoffPassed = false;
    }
  }

  const showJackpotGuaranteeAmount =
    data.showJackpotGuaranteeAmount === true ||
    data.show_jackpot_guarantee_amount === true ||
    data.ShowJackpotGuaranteeAmount === true;

  const gameTypeVal = data.gameType || data.game_type || null;
  const is5050 = gameTypeVal === 'classic5050' || gameTypeVal === 'modified5050' || gameTypeVal === 'progressive5050' || String(gameTypeVal).includes('5050');

  const rawActualJackpot = normalizeAmount(
    data.actualJackpotAmount ??
    data.ActualJackpotAmount ??
    data.actual_jackpot_amount
  );

  const actual_jackpot_amount = (is5050 && rawActualJackpot !== null)
    ? Math.floor(rawActualJackpot / 2)
    : rawActualJackpot;

  const current_jackpot = normalizeAmount(
    data.current_jackpot ??
    data.currentJackpot ??
    data.CurrentJackpot ??
    data.currentJackpotAmount ??
    data.actualJackpotAmount ??
    data.ActualJackpotAmount ??
    data.nextDrawAmount ??
    data.jackpotAmount
  ) ?? null;

  return {
    id: data.id || data.drawId || data.draw_id,
    name: data.name || data.drawName || data.draw_name || data.nextDrawTitle || null,
    title: data.title || data.drawName || data.draw_name || data.nextDrawTitle || data.name || fallbackTitle,
    draw_date: normalizedDrawDateValue,
    cutoff_date_passed: cutoffPassed,
    current_jackpot,
    total_tickets_sold: data.total_tickets_sold ?? data.totalTicketsSold ?? null,
    max_tickets: data.max_tickets ?? data.maxTickets ?? null,
    cards_remaining: data.cards_remaining ?? data.cardsRemaining ?? data.cardsRemainingCount ?? null,
    secondary_prize: normalizeAmount(
      data.secondary_prize ??
      data.weeklyPrizeResolvedAmount ??
      data.weeklyPrizeAmount ??
      data.weeklyPrize ??
      data.currentSecondaryJackpot ??
      data.CurrentSecondaryJackpot ??
      data.secondaryJackpot ??
      data.secondaryPrize ??
      data.actualSecondaryJackpotAmount ??
      data.ActualWeeklyAmount
    ) ?? null,
    weekly_prize: normalizeAmount(
      data.weekly_prize ??
      data.weeklyPrizeResolvedAmount ??
      data.weeklyPrizeAmount ??
      data.weeklyPrize ??
      data.currentSecondaryJackpot ??
      data.CurrentSecondaryJackpot ??
      data.secondary_prize ??
      data.secondaryPrize ??
      data.ActualWeeklyAmount
    ) ?? null,
    eliminated_cards: data.eliminated_cards || data.eliminatedCards || null,
    description: data.description || data.drawDescription || '',
    charity_name: data.charity_name || data.charityName || data.organizationName || null,
    ticket_price: normalizeAmount(data.ticket_price ?? data.ticketPrice ?? data.ticketCost) ?? null,
    partner_name: data.partner_name || data.partnerName,
    partner_logo_url: data.partner_logo_url || data.partnerLogoUrl,
    partner_description: data.partner_description || data.partnerDescription,
    // Pass-through fields from NextDrawData for layout components
    game_type: data.gameType || data.game_type || null,
    status: data.status || null,
    time_zone: data.timeZone || data.time_zone || null,
    next_draw_time: data.nextDrawTime || data.next_draw_time || null,
    opening_date: data.openingDate || data.opening_date || null,
    is_prize: data.isPrize ?? data.is_prize ?? null,
    prize_name: (data.prizeName && data.prizeName.trim()) || (data.prize_name && data.prize_name.trim()) || pickLocalized(data.prizeNames || data.prize_names, options.language, '') || null,
    prize_description: (data.prizeDescription && data.prizeDescription.trim()) || (data.prize_description && data.prize_description.trim()) || (data.prizeDesc && data.prizeDesc.trim()) || pickLocalized(data.prizeDescriptions || data.prize_descriptions, options.language, '') || null,
    prize_descriptions: data.prizeDescriptions || data.prize_descriptions || null,
    prize_names: data.prizeNames || data.prize_names || null,
    prize_value: normalizeAmount(data.prizeValue ?? data.prize_value ?? data.prizeAmount ?? data.prize_amount) ?? null,
    draw_number: data.drawNumber ?? data.draw_number ?? data.DrawNumber ?? null,
    name: data.name || data.drawName || data.draw_name || data.nextDrawTitle || null,
    week_number: data.currentWeekNumber ?? data.weekNumber ?? data.drawWeekNumber ?? data.current_week_number ?? data.week_number ?? null,
    game_number: data.currentGameNumber ?? data.gameNumber ?? data.current_game_number ?? data.game_number ?? null,
    current_week_number: data.currentWeekNumber ?? data.current_week_number ?? null,
    current_game_number: data.currentGameNumber ?? data.current_game_number ?? null,
    secondary_pot_percentage: data.secondaryPotPercentage ?? data.secondary_pot_percentage ?? null,
    weekly_prize_mode: data.weeklyPrizeMode ?? data.ctaWeeklyPrizeMode ?? data.weekly_prize_mode ?? null,
    weekly_prize_base_amount: normalizeAmount(data.weeklyPrizeBaseAmount ?? data.weekly_prize_base_amount) ?? null,
    weekly_prize_guarantee_funding_amount: normalizeAmount(data.weeklyPrizeGuaranteeFundingAmount ?? data.weekly_prize_guarantee_funding_amount) ?? null,
    weekly_prize_guarantee_behavior: data.weeklyPrizeGuaranteeBehavior ?? data.ctaWeeklyPrizeGuaranteeBehavior ?? data.weekly_prize_guarantee_behavior ?? null,
    progressive_jackpot_percentage: data.progressiveJackpotPercentage ?? data.progressive_jackpot_percentage ?? null,
    utc_offset: utcOffset,
    show_jackpot_guarantee_text: data.showJackpotGuaranteeText ?? data.ShowJackpotGuaranteeText ?? data.show_jackpot_guarantee_text ?? null,
    show_weekly_guarantee_text: data.showWeeklyGuaranteeText ?? data.ShowWeeklyGuaranteeText ?? data.show_weekly_guarantee_text ?? null,
    show_jackpot_guarantee_amount: data.showJackpotGuaranteeAmount ?? data.show_jackpot_guarantee_amount ?? null,
    show_weekly_guarantee_amount: data.showWeeklyGuaranteeAmount ?? data.show_weekly_guarantee_amount ?? null,
    actual_jackpot_amount: normalizeAmount(data.actualJackpotAmount ?? data.ActualJackpotAmount ?? data.actual_jackpot_amount) ?? null,
    actual_weekly_amount: normalizeAmount(data.actualWeeklyAmount ?? data.ActualWeeklyAmount ?? data.actual_weekly_amount) ?? null,
    // Fundraising goal (OrganizationGoal)
    goal_enabled: data.goalEnabled ?? data.GoalEnabled ?? data.goal_enabled ?? false,
    goal_amount: normalizeAmount(data.goalAmount ?? data.GoalAmount ?? data.goal_amount) ?? 0,
    goal_amount_raised: normalizeAmount(data.goalAmountRaised ?? data.GoalAmountRaised ?? data.goal_amount_raised) ?? 0,
    goal_progress_percent: normalizeAmount(data.goalProgressPercent ?? data.GoalProgressPercent ?? data.goal_progress_percent) ?? 0,
    goal_theme: data.goalTheme ?? data.GoalTheme ?? data.goal_theme ?? 'classic-red',
    goal_title: data.goalTitle ?? data.GoalTitle ?? data.goal_title ?? '',
    goal_scope: data.goalScope ?? data.GoalScope ?? data.goal_scope ?? '',
    content_location: data.contentLocation || data.content_location || null,
  };
};

export const mapNextDrawForFrontend = (data, options = {}) => {
  const mapped = mapDrawFromApi(data, options);
  if (!mapped) return null;

  return {
    ...data,
    ...mapped,
    drawDate: mapped.draw_date,
    currentJackpot: mapped.current_jackpot,
    jackpot: mapped.current_jackpot,
    prizePool: mapped.current_jackpot,
    totalTicketsSold: mapped.total_tickets_sold,
  };
};

/**
 * Non-destructively merges a SignalR purchase/draw update into an existing
 * frontend draw state object.
 *
 * SignalR `userPurchase` payloads only include a sparse `PushNextDraw` subset
 * (`DrawDate`, `CurrentJackpot`, `CurrentSecondaryJackpot`, `ActualJackpotAmount`,
 * `ActualWeeklyAmount`, `ShowWeeklyGuaranteeText`, `ShowJackpotGuaranteeText`).
 * Passing that sparse object through `mapNextDrawForFrontend` alone produces `null`/`false`
 * defaults for 30+ fields (`status`, `game_type`, `opening_date`, `ticket_price`, `goal_*`, etc.)
 * and omits `utcOffset`. This helper preserves all existing metadata on `currentDraw`
 * and only applies meaningful positive/present fields from the incoming event.
 */
export const mergeSignalRDrawUpdate = (currentDraw, incomingData, options = {}) => {
  if (!incomingData) return currentDraw;

  const rawEnvelope = unwrapOperationData(incomingData);
  if (!rawEnvelope || typeof rawEnvelope !== 'object') return currentDraw;

  const rawData = rawEnvelope.nextDraw || rawEnvelope.NextDraw || rawEnvelope;
  if (!rawData || typeof rawData !== 'object') return currentDraw;

  const incomingId =
    rawData.id || rawData.Id || rawData.drawId || rawData.DrawId || rawEnvelope.drawId || rawEnvelope.DrawId || null;

  if (currentDraw?.id && incomingId && String(currentDraw.id) !== String(incomingId)) {
    return currentDraw;
  }

  const hasExplicitUtcOffset = rawData.utcOffset != null || rawData.utc_offset != null;
  const resolvedUtcOffset =
    rawData.utcOffset ?? rawData.utc_offset ?? currentDraw?.utc_offset ?? currentDraw?.utcOffset ?? null;
  const resolvedGameType =
    rawData.gameType || rawData.game_type || currentDraw?.game_type || currentDraw?.gameType || options.gameType || null;

  const mapped = mapDrawFromApi(
    {
      ...rawData,
      ...(incomingId ? { id: incomingId } : {}),
      ...(resolvedUtcOffset != null ? { utcOffset: resolvedUtcOffset } : {}),
      ...(resolvedGameType ? { gameType: resolvedGameType } : {}),
    },
    options
  );

  if (!mapped) return currentDraw;

  if (!currentDraw) {
    return {
      ...rawData,
      ...mapped,
      drawDate: mapped.draw_date,
      currentJackpot: mapped.current_jackpot,
      jackpot: mapped.current_jackpot,
      prizePool: mapped.current_jackpot,
      totalTicketsSold: mapped.total_tickets_sold,
    };
  }

  const jackpot =
    mapped.current_jackpot != null && mapped.current_jackpot > 0
      ? mapped.current_jackpot
      : (currentDraw.current_jackpot ?? currentDraw.currentJackpot ?? currentDraw.jackpot ?? mapped.current_jackpot ?? null);

  const secondaryPrize =
    mapped.secondary_prize != null && mapped.secondary_prize > 0
      ? mapped.secondary_prize
      : (currentDraw.secondary_prize ?? null);

  const weeklyPrize =
    mapped.weekly_prize != null && mapped.weekly_prize > 0
      ? mapped.weekly_prize
      : (currentDraw.weekly_prize ?? null);

  const actualJackpot =
    mapped.actual_jackpot_amount != null && mapped.actual_jackpot_amount > 0
      ? mapped.actual_jackpot_amount
      : (currentDraw.actual_jackpot_amount ?? null);

  const actualWeekly =
    mapped.actual_weekly_amount != null && mapped.actual_weekly_amount > 0
      ? mapped.actual_weekly_amount
      : (currentDraw.actual_weekly_amount ?? null);

  const hasShowJackpotGuarantee =
    rawData.showJackpotGuaranteeText !== undefined ||
    rawData.ShowJackpotGuaranteeText !== undefined ||
    rawData.show_jackpot_guarantee_text !== undefined;

  const hasShowWeeklyGuarantee =
    rawData.showWeeklyGuaranteeText !== undefined ||
    rawData.ShowWeeklyGuaranteeText !== undefined ||
    rawData.show_weekly_guarantee_text !== undefined;

  const drawDate =
    mapped.draw_date && (hasExplicitUtcOffset || resolvedUtcOffset != null || !currentDraw.draw_date)
      ? mapped.draw_date
      : (currentDraw.draw_date ?? currentDraw.drawDate ?? null);

  const totalTicketsSold =
    mapped.total_tickets_sold ?? currentDraw.total_tickets_sold ?? currentDraw.totalTicketsSold ?? null;

  const cardsRemaining =
    mapped.cards_remaining ?? currentDraw.cards_remaining ?? currentDraw.cardsRemaining ?? null;

  return {
    ...currentDraw,
    ...(incomingId ? { id: incomingId } : {}),
    ...(drawDate ? { draw_date: drawDate, drawDate } : {}),
    ...(mapped.draw_number != null ? { draw_number: mapped.draw_number } : {}),
    current_jackpot: jackpot,
    currentJackpot: jackpot,
    jackpot,
    prizePool: jackpot,
    actual_jackpot_amount: actualJackpot,
    actual_weekly_amount: actualWeekly,
    secondary_prize: secondaryPrize,
    weekly_prize: weeklyPrize,
    total_tickets_sold: totalTicketsSold,
    totalTicketsSold,
    cards_remaining: cardsRemaining,
    ...(hasShowJackpotGuarantee ? { show_jackpot_guarantee_text: mapped.show_jackpot_guarantee_text } : {}),
    ...(hasShowWeeklyGuarantee ? { show_weekly_guarantee_text: mapped.show_weekly_guarantee_text } : {}),
  };
};

// Map an AdditionalDraw API record into the shape BonusDrawsList expects.
// AdditionalDraw payload uses: prizeNames/prizeDescriptions (localized dicts),
// prizeValue, prizeGraphicUrl, drawDate, drawCutOffDate, sortOrder.
export const mapBonusDraw = (draw, options = {}) => {
  if (!draw) return null;
  const { fallbackTitle = 'Prize Draw', language = 'English' } = options;

  const prizeName = pickLocalized(draw.prizeNames, language, '');
  const prizeDescription = pickLocalized(draw.prizeDescriptions, language, '');
  const prizeGraphicUrl = pickLocalized(draw.prizeGraphicUrls, language, '');

  return {
    ...draw,
    id: draw.id || draw.drawId,
    sort_order: draw.sortOrder ?? null,
    title: prizeName || draw.title || fallbackTitle,
    description: prizeDescription || '',
    draw_date: draw.drawDate || draw.draw_date,
    draw_cutoff_date: draw.drawCutOffDate || draw.draw_cutoff_date,
    opening_date: draw.openingDate || draw.opening_date,
    current_jackpot: normalizeAmount(draw.prizeValue ?? draw.current_jackpot) ?? 0,
    prize_value: normalizeAmount(draw.prizeValue ?? draw.prize_value) ?? 0,
    image_url: prizeGraphicUrl || draw.prizeGraphicUrl || draw.image_url || null,
    is_claimed: draw.isClaimed ?? false,
    is_early_bird: draw.isEarlyBird ?? false,
    ticket_number: draw.ticketNumber ?? null,
    confirmation_code: draw.confirmationCode ?? null,
    number_of_tickets: draw.numberOfTickets ?? null,
    total_tickets_sold: draw.total_tickets_sold ?? draw.totalTicketsSold ?? null,
    draw_number: draw.drawNumber ?? draw.draw_number ?? draw.DrawNumber ?? null,
  };
};

export const mapBonusDraws = (data, options = {}) => {
  if (!Array.isArray(data)) return [];
  return data
    .map((draw) => mapBonusDraw(draw, options))
    .filter(Boolean)
    .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
};
